// src/components/DocumentViewer.jsx
// A client document shown inside the portal (3.68.0, owner, Oct 26), in a
// panel like the fax preview, with Download. The file comes from our own
// backend after the sign-in check (fileUrl: …/documents/file?…), never from
// a storage link, so nothing opens outside the portal. PDFs use the
// browser's viewer on computers and are drawn as pages on phones (PdfPages);
// pictures show as they are; Word files can only be downloaded.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from './Notifications';
import { usePhone } from '../hooks/useWindowWidth';
import PdfPages, { needsPdfPages } from './PdfPages';
import Loader from './Loader';
import Emo from './Emo';

// doc: { fileName, typeLabel }; fileUrl: the backend address of the file
export default function DocumentViewer({ doc, fileUrl, onClose }) {
  const { getToken } = useAuth();
  const { notify } = useNotify();
  const phone = usePhone();
  const closeRef = useRef(null);
  const [file, setFile] = useState({ state: 'loading' });   // { state, url, mime, blob, error }

  useEffect(() => {
    let url = null, live = true;
    (async () => {
      try {
        const r = await fetch(fileUrl, { headers: { Authorization: `Bearer ${await getToken()}` } });
        if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || "Couldn't open the document."); }
        const blob = await r.blob();
        url = URL.createObjectURL(blob);
        if (live) setFile({ state: 'ready', url, mime: blob.type || '', blob });
      } catch (e) { if (live) setFile({ state: 'error', error: e.message }); }
    })();
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [fileUrl, getToken]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Downloads ask the backend again (download=1), so they're in the Activity log
  async function download() {
    try {
      const r = await fetch(`${fileUrl}&download=1`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || "Couldn't download the document."); }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a');
      a.href = url; a.download = doc.fileName;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { notify({ tone: 'error', title: 'Download failed', detail: e.message }); }
  }

  // On top of the page, so a dialog it's opened from can't clip it
  return createPortal(
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <aside style={phone ? { ...S.panel, width: '100vw', boxShadow: 'none' } : S.panel} role="dialog" aria-modal="true" aria-labelledby="doc-viewer-title">
        <div style={phone ? { ...S.header, padding: '12px 14px' } : S.header} className="dlg-head">
          <div style={{ minWidth: 0 }}>
            <div style={S.eyebrow}>{doc.typeLabel || 'Document'}</div>
            <h2 id="doc-viewer-title" style={phone ? { ...S.title, fontSize: 17 } : S.title}>{doc.fileName}</h2>
          </div>
          <div style={S.headerBtns}>
            <button onClick={download} disabled={file.state === 'error'} style={S.downloadBtn} title="Download this document">
              <Emo e="⬇️" /> Download
            </button>
            <button ref={closeRef} onClick={onClose} style={phone ? { ...S.closeBtn, width: 40, height: 40 } : S.closeBtn} title="Close (Esc)" aria-label="Close">✕</button>
          </div>
        </div>
        <div style={phone ? { ...S.body, padding: 14 } : S.body}>
          {file.state === 'loading' ? <div style={S.empty}><Loader size="sm" label="Loading the document…" /></div>
            : file.state === 'error' ? (
              <div style={S.empty}><div style={S.icon}><Emo e="⚠️" /></div><div style={S.msg}>Can't show this document</div><div style={S.muted}>{file.error}</div></div>
            ) : file.mime === 'application/pdf' ? (
              needsPdfPages() ? <PdfPages blob={file.blob} fileName={doc.fileName} />
                : <iframe src={`${file.url}#navpanes=0&view=FitH`} title={doc.fileName} style={S.frame} />
            ) : file.mime.startsWith('image/') ? (
              <div style={S.imageWrap}><img src={file.url} alt={doc.fileName} style={{ display: 'block', width: '100%' }} /></div>
            ) : (
              <div style={S.empty}>
                <div style={S.icon}><Emo e="📄" /></div>
                <div style={S.msg}>No preview for this file type</div>
                <div style={S.muted}>Word documents can't be shown in the browser.</div>
                <button onClick={download} style={{ ...S.downloadBtn, marginTop: 12 }}>Download to view</button>
              </div>
            )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.35)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' },
  panel: {
    width: 'min(760px, 100vw)', height: '100%', background: 'var(--c-ffffff)', display: 'flex', flexDirection: 'column',
    boxShadow: '-12px 0 40px rgb(var(--c-000000-rgb) / 0.18)', animation: 'slideIn 0.18s ease-out',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
    padding: '18px 20px', borderBottom: '2px solid var(--c-e0f2f1)', background: 'var(--c-f9fafb)',
  },
  eyebrow: { fontSize: 11, fontWeight: 700, color: 'var(--t-78909c)', textTransform: 'uppercase', letterSpacing: '0.6px' },
  title: { margin: '2px 0 0', fontSize: 19, fontWeight: 700, color: 'var(--t-00695c)', fontFamily: 'inherit', overflowWrap: 'anywhere' },
  headerBtns: { display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' },
  downloadBtn: {
    padding: '7px 14px', borderRadius: 8, border: '1.5px solid var(--c-b2dfdb)', background: 'var(--c-ffffff)',
    color: 'var(--t-00695c)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  closeBtn: {
    width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)',
    cursor: 'pointer', fontSize: 15, color: 'var(--t-546e7a)', fontFamily: 'inherit',
  },
  body: { flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column' },
  frame: { width: '100%', flex: 1, minHeight: 420, border: '1px solid var(--c-e0e0e0)', borderRadius: 10, background: 'var(--c-f4f7f6)' },
  imageWrap: { border: '1px solid var(--c-e0e0e0)', borderRadius: 10, background: 'var(--c-f4f7f6)', overflow: 'auto' },
  empty: {
    border: '1.5px dashed var(--c-cfd8dc)', borderRadius: 10, background: 'var(--c-fafafa)', padding: '36px 20px',
    textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
  },
  icon: { fontSize: 30 },
  msg: { fontSize: 15, fontWeight: 600, color: 'var(--t-37474f)' },
  muted: { fontSize: 13, color: 'var(--t-607d8b)', lineHeight: 1.5 },
};
